import React, { memo, useCallback, useState, useEffect, useRef, useMemo } from 'react';
import {
  TextField,
  Box,
  Popper,
  Grow,
  ClickAwayListener,
  List,
  ListItem,
  ListItemText,
  CircularProgress,
  StandardTextFieldProps,
  Skeleton,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import { Controller } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { AlyceTheme, Icon } from '@alycecom/ui';
import { EntityId } from '@alycecom/utils';
import { useDebounce, useMount } from 'react-use';

import { ShippingAddressField } from '../../../../store/shipping/shipping.types';
import { IVerifiedAddressOption } from '../../../../types/Shipping.types';
import {
  getAddresses,
  getAddressesIds,
  getAddressesMap,
  getIsAddressesLoaded,
  getIsAddressesLoading,
} from '../../../../store/address';
import { loadAddressRequest } from '../../../../store/address/address.actions';
import { getGiftHashId } from '../../../../store/data/data.selectors';
import useTrackRecipient from '../../../../../../hooks/useTrackRecipient';

import { TFieldProps } from './Fields';

const useStyles = makeStyles<AlyceTheme>(({ palette, spacing, zIndex, breakpoints }) => ({
  wrapper: {
    position: 'relative',
  },
  popper: {
    zIndex: zIndex.tooltip,
    border: `1px solid ${palette.primary.main}`,
    borderRadius: 5,
    backgroundColor: palette.common.white,
    width: 484,
    left: '-38px !important',
    marginTop: `${spacing(1)} !important`,
    [breakpoints.down('lg')]: {
      maxWidth: 400,
      width: 400,
    },
    [breakpoints.down('sm')]: {
      maxWidth: 320,
      width: 320,
      transform: 'translate3d(38px, -270px, 0px) !important',
    },
  },
  list: {
    [breakpoints.down('sm')]: {
      maxHeight: 250,
      overflow: 'auto',
    },
  },
  listOption: {
    marginTop: 6,
    marginBottom: 6,
  },
  listOptionText: {
    fontSize: 16,
    fontWeight: 'normal',
    fontStretch: 'normal',
    fontStyle: 'normal',
    lineHeight: 1.5,
    letterSpacing: 'normal',
    color: palette.text.primary,
  },
  searchIcon: {
    marginRight: spacing(0.5),
  },
}));

type TAddressAutocompleteProps = TFieldProps<'watch'> &
  Omit<StandardTextFieldProps, 'error' | 'onChange'> & {
    onSelectAddress: (option: IVerifiedAddressOption) => void;
  };

const MIN_SEARCH_LENGTH = 3;

const AddressAutocomplete = ({
  control,
  disabled,
  watch,
  error,
  onSelectAddress,
  ...fieldProps
}: TAddressAutocompleteProps): JSX.Element => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const trackEvent = useTrackRecipient();

  const addresses = useSelector(getAddresses);
  const isLoading = useSelector(getIsAddressesLoading);
  const isLoaded = useSelector(getIsAddressesLoaded);
  const hashId = useSelector(getGiftHashId);

  const addressesMap = useSelector(getAddressesMap);
  const addressesIdsFromState = useSelector(getAddressesIds);

  const inputRef = useRef(null);
  const isMount = useRef(false);

  const [anchorEl, setAnchorEl] = useState(null);
  const [search, setSearch] = useState('');

  const isOpen = !!anchorEl;
  const id = isOpen ? `AddressAutocomplete.${ShippingAddressField.Address}` : undefined;

  const { id: countryId } = watch(ShippingAddressField.Country);

  const addressesIds = useMemo<EntityId[] | undefined[]>(
    () => (isLoading ? (Array.from({ length: 5 }) as EntityId[]) : addressesIdsFromState),
    [isLoading, addressesIdsFromState],
  );

  const handleOpen = useCallback(
    event => {
      if (isLoaded && addresses.length > 0) {
        setAnchorEl(event.currentTarget);
      }
    },
    [isLoaded, addresses],
  );

  const handleClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const handleOnChangeSearch = useCallback(
    ({ target: { value } }: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
      setSearch(value);
    },
    [],
  );

  const handleSelectAddress = useCallback(
    (option: IVerifiedAddressOption) => {
      onSelectAddress(option);
      trackEvent('Autocomplete — suggestion selected');
      setAnchorEl(null);
    },
    [onSelectAddress, trackEvent],
  );

  useEffect(() => {
    if (isLoaded) {
      const isAddressListVisible = search.length >= MIN_SEARCH_LENGTH && addresses.length > 0;
      setAnchorEl(isAddressListVisible ? inputRef.current : null);
    }
  }, [isLoaded, addresses, search]);

  useDebounce(
    () => {
      if (isMount.current && search.length >= MIN_SEARCH_LENGTH) {
        dispatch(loadAddressRequest({ hashId, countryId, address: search }));
      }
    },
    300,
    [search, hashId, countryId, dispatch],
  );

  useMount(() => {
    isMount.current = true;
  });

  return (
    <Controller
      control={control}
      name={ShippingAddressField.Address}
      render={({ value, onChange }) => (
        <Box className={classes.wrapper}>
          <TextField
            inputRef={inputRef}
            label="Start typing address"
            placeholder="Start typing address"
            name={ShippingAddressField.Address}
            fullWidth
            variant="outlined"
            error={!!error}
            helperText={error}
            disabled={disabled}
            value={value}
            onChange={event => {
              onChange(event);
              handleOnChangeSearch(event);
            }}
            data-testid="ShippingAddressForm.RecipientAddress"
            data-private="lipsum"
            autoComplete="nope"
            onFocus={handleOpen}
            {...fieldProps}
            InputProps={{
              startAdornment: <Icon className={classes.searchIcon} icon="search" color="grey.main" />,
              endAdornment: <>{isLoading ? <CircularProgress color="inherit" size={20} /> : null}</>,
            }}
          />
          <Popper
            id={id}
            open={isOpen}
            transition
            anchorEl={anchorEl}
            placement="bottom-start"
            className={classes.popper}
            disablePortal
          >
            {({ TransitionProps, placement }) => (
              <Grow
                {...TransitionProps}
                style={{ transformOrigin: placement === 'bottom' ? 'center top' : 'center bottom' }}
              >
                <div>
                  <ClickAwayListener onClickAway={handleClose}>
                    <List className={classes.list}>
                      {addressesIds.map((addressId: EntityId | undefined, index: number) => (
                        <ListItem
                          classes={{
                            dense: classes.listOption,
                          }}
                          key={addressId ?? index}
                          role="button"
                          dense
                          button
                          onClick={() => {
                            if (addressId) {
                              handleSelectAddress(addressesMap[addressId]);
                            }
                          }}
                        >
                          {addressId ? (
                            <ListItemText
                              classes={{
                                primary: classes.listOptionText,
                              }}
                              className="Body-Regular-Left-Static"
                              primary={addressesMap[addressId].formattedAddress}
                            />
                          ) : (
                            <Box width={1} my={0.5}>
                              <Skeleton width="100%" height={24} />
                            </Box>
                          )}
                        </ListItem>
                      ))}
                    </List>
                  </ClickAwayListener>
                </div>
              </Grow>
            )}
          </Popper>
        </Box>
      )}
    />
  );
};

export default memo<TAddressAutocompleteProps>(AddressAutocomplete);
